import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { EMPTY, from, Observable, catchError, distinctUntilChanged, finalize, map, of, shareReplay, startWith, switchMap, tap } from "rxjs";
import { SharedModule } from "src/app/shared.module";
import { DropdownComponent } from "../../../components/ui-components/dropdown/dropdown.component";
import { FormContainerComponent } from "../../../components/ui-components/forms/form-container/form-container.component";
import { FormInputComponent } from "../../../components/ui-components/forms/form-input/form-input.component";
import { FormTextareaComponent } from "../../../components/ui-components/forms/form-textarea/form-textarea.component";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import { DropdownOption } from "../../../components/ui-components/dropdown/dropdown.model";
import { SharedApiService } from "src/app/services/api/sharedApi.service";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { IExecuteProcedureRequest, IExecuteProcedureResult, IGetCashMovementsSummaryRequest, IGetCashMovementsSummaryResponse, IGetPaymentTypesResultData } from "src/app/services/model/pos.api.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { PaymentTypesService } from "src/app/services/payment-types.service";
import { LoginService, PopUpDialogInformalService, PosDataService } from "@dert-services";
import { PopUpInformalType } from "src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { HeaderProcessingService } from "src/app/services/header-processing.service";
import { ReportExportService } from "src/app/services/report-export.service";
import {
  DenominationItem,
  FinancialTransactionType,
  TransactionTypeCodes,
  TransferTransactionRequest,
  TransferTransactionResult,
  extractTrxUID,
} from "src/app/services/model/create-financial-transaction.model";
import { generateGuid } from "src/app/functions/common-functions";

@Component({
  selector: "app-voucher-exchange",
  templateUrl: "./voucher-exchange.component.html",
  styleUrls: ["./voucher-exchange.component.scss"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    FormContainerComponent,
    FormInputComponent,
    FormTextareaComponent,
    TranslateModule,
    SharedModule,
  ],
  standalone: true,
})
export class VoucherExchangeComponent {
  FormInputType = FormInputType;

  private readonly paymentTypesService = inject(PaymentTypesService);
  private readonly loginService = inject(LoginService);
  private readonly posDataService = inject(PosDataService);
  private readonly sharedApiService = inject(SharedApiService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly translate = inject(TranslateService);
  private readonly popUpDialogInformalService = inject(PopUpDialogInformalService);
  private readonly headerProcessingService = inject(HeaderProcessingService);
  private readonly reportExportService = inject(ReportExportService);

  @Input() fromPaymentType: string | null = null;
  @Output() onClose = new EventEmitter<void>();

  voucherPaymentTypes: IGetPaymentTypesResultData[] = [];
  voucherTypeOptions: DropdownOption[] = [];
  fromNominalOptions: DropdownOption[] = [];
  toNominalOptions: DropdownOption[] = [];

  private readonly availableAmountCache = new Map<string, Promise<number | null>>();

  // Whether the currently selected payment type has nominals configured
  fromHasNominals = false;
  toHasNominals = false;

  form = new FormGroup(
    {
      fromPaymentType: new FormControl<string | null>(null, Validators.required),
      fromNominal: new FormControl<number | null>(null),
      fromPieces: new FormControl<number | null>(null),
      fromValue: new FormControl<number | null>(null),
      toPaymentType: new FormControl<string | null>(null, Validators.required),
      toNominal: new FormControl<number | null>(null),
      toPieces: new FormControl<number | null>(null),
      toValue: new FormControl<number | null>(null),
      note: new FormControl<string>("", Validators.required),
    },
    {
      validators: this.exchangeValueValidator(),
      asyncValidators: this.exchangeAvailabilityValidator(),
    },
  );

  readonly fromAvailableAmount = toSignal(this.buildAvailableAmountStream(this.form.controls.fromPaymentType), { initialValue: null });

  private static readonly EXCLUDED_PAYMENT_NAMES = new Set([
    'BEN VOUCHER CZ',
    'BEN VOUCHER SK',
    'POUK SMARTWINGS',
    'POUK SKODA',
    'VOUCHER CZ',
    'VOUCHER SK',
  ]);

  ngOnInit() {
    this.spinner.show();
    from(this.paymentTypesService.getPaymentTypesWithNominals())
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe((allTypes) => {
        this.voucherPaymentTypes = (allTypes ?? []).filter(
          (pt) =>
            (pt.TenderType === TenderType.Voucher ||
              pt.TenderType === TenderType.Cheque ||
              pt.TenderType === TenderType.Cash) &&
            !VoucherExchangeComponent.EXCLUDED_PAYMENT_NAMES.has(pt.Name),
        );
        this.voucherTypeOptions = this.voucherPaymentTypes.map((pt) => ({
          value: pt.Name,
          label: pt.Description,
        }));

        if (this.fromPaymentType && this.voucherPaymentTypes.some((pt) => pt.Name === this.fromPaymentType)) {
          this.form.controls.fromPaymentType.setValue(this.fromPaymentType);
        }
      });

    this.form.controls.fromPaymentType.valueChanges.subscribe((name) => {
      const pt = this.voucherPaymentTypes.find((p) => p.Name === name);
      this.fromHasNominals = this.paymentTypeHasNominals(pt);
      this.fromNominalOptions = this.buildNominalOptions(name);
      this.resetSideControls('from');
      this.updateSideValidators('from');
    });

    this.form.controls.toPaymentType.valueChanges.subscribe((name) => {
      const pt = this.voucherPaymentTypes.find((p) => p.Name === name);
      this.toHasNominals = this.paymentTypeHasNominals(pt);
      this.toNominalOptions = this.buildNominalOptions(name);
      this.resetSideControls('to');
      this.updateSideValidators('to');
    });
  }

  private paymentTypeHasNominals(paymentType: IGetPaymentTypesResultData | undefined): boolean {
    return paymentType?.TenderType !== TenderType.Cash && !!paymentType?.Nominals?.length;
  }

  private resetSideControls(side: 'from' | 'to'): void {
    this.form.get(`${side}Nominal`)!.setValue(null);
    this.form.get(`${side}Pieces`)!.setValue(null);
    this.form.get(`${side}Value`)!.setValue(null);
  }

  private updateSideValidators(side: 'from' | 'to'): void {
    const hasNominals = side === 'from' ? this.fromHasNominals : this.toHasNominals;
    const nominalCtrl = this.form.get(`${side}Nominal`)!;
    const piecesCtrl  = this.form.get(`${side}Pieces`)!;
    const valueCtrl   = this.form.get(`${side}Value`)!;

    if (hasNominals) {
      nominalCtrl.setValidators([Validators.required, Validators.min(0.01)]);
      piecesCtrl.setValidators([Validators.required, Validators.min(1)]);
      valueCtrl.clearValidators();
    } else {
      nominalCtrl.clearValidators();
      piecesCtrl.clearValidators();
      valueCtrl.setValidators([Validators.required, Validators.min(0.01)]);
    }

    nominalCtrl.updateValueAndValidity();
    piecesCtrl.updateValueAndValidity();
    valueCtrl.updateValueAndValidity();
  }

  // Computes total value for one side. Returns null when inputs are incomplete.
  private computeTotal(
    hasNominals: boolean,
    nominal: number | null,
    pieces: number | null,
    value: number | null
  ): number | null {
    if (hasNominals) {
      return nominal != null && pieces != null ? nominal * pieces : null;
    }
    return value;
  }

  get fromTotal(): number | null {
    return this.computeTotal(
      this.fromHasNominals,
      this.form.controls.fromNominal.value,
      this.form.controls.fromPieces.value,
      this.form.controls.fromValue.value
    );
  }

  get toTotal(): number | null {
    return this.computeTotal(
      this.toHasNominals,
      this.form.controls.toNominal.value,
      this.form.controls.toPieces.value,
      this.form.controls.toValue.value
    );
  }

  private buildNominalOptions(paymentName: string | null): DropdownOption[] {
    if (!paymentName) return [];
    const pt = this.voucherPaymentTypes.find((p) => p.Name === paymentName);
    if (!this.paymentTypeHasNominals(pt)) return [];
    return pt.Nominals.map((n) => ({
      value: n.value,
      label: n.denomination,
    }));
  }

  private exchangeValueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) return null;

      // Skip cross-validation until both sides have a payment type selected
      if (!control.get('fromPaymentType')?.value || !control.get('toPaymentType')?.value) {
        return null;
      }

      const fromNominal = control.get('fromNominal')?.value as number | null;
      const fromPieces  = control.get('fromPieces')?.value  as number | null;
      const fromValue   = control.get('fromValue')?.value   as number | null;
      const toNominal   = control.get('toNominal')?.value   as number | null;
      const toPieces    = control.get('toPieces')?.value    as number | null;
      const toValue     = control.get('toValue')?.value     as number | null;

      // Determine mode: nominal mode when nominal or pieces are filled in
      const fromUseNominals = fromNominal != null || fromPieces != null;
      const toUseNominals   = toNominal   != null || toPieces   != null;

      const fromTotal = fromUseNominals
        ? (fromNominal != null && fromPieces != null ? fromNominal * fromPieces : null)
        : fromValue;
      const toTotal = toUseNominals
        ? (toNominal != null && toPieces != null ? toNominal * toPieces : null)
        : toValue;

      if (fromTotal == null || toTotal == null) return null;

      return Math.round(fromTotal * 100) === Math.round(toTotal * 100)
        ? null
        : { exchangeValueMismatch: true };
    };
  }

  private exchangeAvailabilityValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!(control instanceof FormGroup)) {
        return of(null);
      }

      const fromPaymentType = control.get('fromPaymentType')?.value as string | null;
      const fromTotal = this.getSideTotal('from', control);

      if (!fromPaymentType || fromTotal == null || fromTotal <= 0) {
        return of(null);
      }

      return from(this.getAvailableAmount(fromPaymentType)).pipe(
        map((fromAvailableAmount) => {
          if (fromAvailableAmount !== null && fromTotal > fromAvailableAmount) {
            return { fromAmountExceeded: true };
          }

          return null;
        })
      );
    };
  }

  private getSideTotal(side: 'from' | 'to', control: AbstractControl): number | null {
    const hasNominals = side === 'from' ? this.fromHasNominals : this.toHasNominals;
    const nominal = control.get(`${side}Nominal`)?.value as number | null;
    const pieces = control.get(`${side}Pieces`)?.value as number | null;
    const value = control.get(`${side}Value`)?.value as number | null;

    if (hasNominals) {
      return nominal != null && pieces != null ? nominal * pieces : null;
    }

    return value;
  }

  private buildAvailableAmountStream(paymentTypeControl: FormControl<string | null>): Observable<number | null> {
    return paymentTypeControl.valueChanges.pipe(
      startWith(paymentTypeControl.value),
      distinctUntilChanged(),
      switchMap((paymentName) => paymentName ? from(this.getAvailableAmount(paymentName)) : of(null)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private async getAvailableAmount(paymentName: string): Promise<number | null> {
    const cachedAmount = this.availableAmountCache.get(paymentName);
    if (cachedAmount) {
      return cachedAmount;
    }

    const amountPromise = this.fetchAvailableAmount(paymentName).catch((error) => {
      this.availableAmountCache.delete(paymentName);
      throw error;
    });

    this.availableAmountCache.set(paymentName, amountPromise);
    return amountPromise;
  }

  private async fetchAvailableAmount(paymentName: string): Promise<number | null> {
    try {
      const jsonData = {
        CurrencyCode: this.loginService.user?.CurrencyCode,
        PaymentName: paymentName,
        ReturnToResult: 0,
      } as IGetCashMovementsSummaryRequest;

      const request: IExecuteProcedureRequest<IGetCashMovementsSummaryRequest> = {
        DbScheme: DbSchemeEnum.Dert,
        ProcedureName: ProcedureNameEnum.GetCashMovementsSummary,
        Parameters: jsonData,
      };

      const result: IExecuteProcedureResult<IGetCashMovementsSummaryResponse> = await this.sharedApiService.executeProcedure<IGetCashMovementsSummaryRequest, IExecuteProcedureResult<IGetCashMovementsSummaryResponse>>(request);

      return result?.data?.TotalMovement ?? null;
    } catch (error) {
      console.error('Failed to load available amount for payment type', paymentName, error);
      return null;
    }
  }

  get valueMismatch(): boolean {
    return this.form.hasError("exchangeValueMismatch") && this.form.touched;
  }

  get fromAmountExceeded(): boolean {
    return this.form.hasError("fromAmountExceeded") && this.form.touched;
  }

  get fromSelectedPaymentType(): IGetPaymentTypesResultData | null {
    return this.voucherPaymentTypes.find((p) => p.Name === this.form.controls.fromPaymentType.value) || null;
  }

  get fallbackCurrencyCode(): string {
    return this.loginService.user.CurrencyCode;
  }

  onConfirm(): void {
    this.form.markAllAsTouched();
    if (this.form.pending || this.form.invalid) return;

    const {
      fromPaymentType, fromNominal, fromPieces, fromValue,
      toPaymentType,   toNominal,   toPieces,   toValue, note,
    } = this.form.value;

    const fromPt       = this.voucherPaymentTypes.find((p) => p.Name === fromPaymentType)!;
    const toPt         = this.voucherPaymentTypes.find((p) => p.Name === toPaymentType)!;
    const fromCurrency = fromPt.Currency ?? this.loginService.user.CurrencyCode;
    const toCurrency   = toPt.Currency   ?? this.loginService.user.CurrencyCode;
    const exchangeTitle = this.translate.instant('webPos.VoucherExchange.Title');
    const additionalJsonData = JSON.stringify({ Note: note ?? '', PairingKey: generateGuid() });

    const fromDenominationItems: DenominationItem[] =
      this.fromHasNominals && fromNominal != null && fromPieces != null
        ? [{ Caption: fromNominal.toString(), Value: fromNominal, Quantity: fromPieces, CurrencyISOCode: fromCurrency }]
        : [];

    const toDenominationItems: DenominationItem[] =
      this.toHasNominals && toNominal != null && toPieces != null
        ? [{ Caption: toNominal.toString(), Value: toNominal, Quantity: toPieces, CurrencyISOCode: toCurrency }]
        : [];

    const request: TransferTransactionRequest = {
      Source: {
        TransactionTypeCode:      TransactionTypeCodes.PickupFromTill,
        FinancialTransactionType: FinancialTransactionType.PickupFromTill,
        TotalPrice:               Math.abs(this.fromTotal!), // Keep as positive value, API expects absolute value and determines direction from FinancialTransactionEffect
        CurrencyISOCode:          fromCurrency,
        TenderType:               fromPt.TenderType,
        TenderSubType:            fromPt.TenderSubType,
        Note:                     note ?? '',
        ReasonCode:               'WITHDRAWAL',
        ReasonText:               exchangeTitle,
        AdditionalJsonData:       additionalJsonData,
        Attributes:               { PaymentType: fromPaymentType!, TenderType: fromPt.TenderType.toString() },
        DenominationItems:        fromDenominationItems,
      },
      Target: {
        TransactionTypeCode:      TransactionTypeCodes.DepositToTill,
        FinancialTransactionType: FinancialTransactionType.DepositToTill,
        TotalPrice:               Math.abs(this.toTotal!), // Keep as positive value, API expects absolute value and determines direction from FinancialTransactionEffect
        CurrencyISOCode:          toCurrency,
        TenderType:               toPt.TenderType,
        TenderSubType:            toPt.TenderSubType,
        Note:                     note ?? '',
        ReasonCode:               'DEPOSIT',
        ReasonText:               exchangeTitle,
        AdditionalJsonData:       additionalJsonData,
        Attributes:               { PaymentType: toPaymentType!, TenderType: toPt.TenderType.toString() },
        DenominationItems:        toDenominationItems,
      },
    };

    this.spinner.show();

    this.posDataService.createTransferTransaction(request)
      .pipe(
        finalize(() => this.spinner.hide()),
        switchMap((result) => this.handleSuccess(result)),
        catchError((err) => {
          console.error('VoucherExchange error', err);
          return from(
            this.popUpDialogInformalService.confirm({
              title:   this.translate.instant('webPos.Errors.Error'),
              message: err?.message ?? '',
              type:    PopUpInformalType.Error,
            })
          ).pipe(switchMap(() => EMPTY));
        })
      )
      .subscribe();
  }

  /** Shows success dialog then optionally prints both documents. */
  private handleSuccess(result: TransferTransactionResult): Observable<void> {
    return from(
      this.popUpDialogInformalService.confirm({
        title:            this.translate.instant('webPos.Done'),
        message:          '',
        type:             PopUpInformalType.Success,
        showPrintReceipt: true,
      })
    ).pipe(
      switchMap((dialogResult) => {
        if (!dialogResult?.printReceipt) {
          this.close();
          return EMPTY;
        }
        return this.printBothDocuments(result);
      }),
      tap(() => this.close())
    );
  }

  /** Prints source document first, then target document sequentially. */
  private printBothDocuments(result: TransferTransactionResult): Observable<void> {
    return this.printDocument(extractTrxUID(result.source)).pipe(
      switchMap(() => this.printDocument(extractTrxUID(result.target)))
    );
  }

  private printDocument(trxUID: string | null): Observable<void> {
    if (!trxUID) return EMPTY;
    return from(this.headerProcessingService.monitorHeader(trxUID)).pipe(
      switchMap((monitorResult) => {
        const headerId = this.headerProcessingService.getHeaderIdFromResult(monitorResult);
        if (!headerId) return EMPTY;
        return from(
          this.reportExportService.downloadPdf(
            undefined,
            [{ key: 'HeaderID', value: headerId.toString() }],
            true
          )
        );
      })
    );
  }

  close(): void {
    this.onClose.emit();
  }
}
